import { useCallback, useState } from "react";
import {
  IntegrationSetupPayload,
  IntegrationSetupStepType,
  LinkedAccountFormData,
  NonStepPageType,
  RegisteredUserFormData
} from "../../../config/types";

import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import IntegrationSetupContext from "./IntegrationSetupContext";
import ConnectorSetupStepSwitcher from "./ConnectorSetupStepSwitcher";

export type SecurityQuestion = {
  question: string;
  answer?: string;
};

export type AllWritableFormDataProperties = {
  [Key in WritableFormDataPropertiesForStepType[keyof WritableFormDataPropertiesForStepType]]: LinkedAccountFormData[Key];
};

const IterableReadableProperties = {
  [IntegrationSetupStepType.STEP_TYPE_USERNAME_PASSWORD]: ["username", "password"],
  [IntegrationSetupStepType.STEP_TYPE_CUSTOM_DOMAIN]: ["customDomain"],
  [IntegrationSetupStepType.STEP_TYPE_CUSTOM_PATH]: ["customPath"],
  [IntegrationSetupStepType.STEP_TYPE_API_KEY]: ["apiKey"],
  [IntegrationSetupStepType.STEP_TYPE_SERVICE_ACCOUNT]: [],
  [IntegrationSetupStepType.STEP_TYPE_SFTP_CREDENTIALS]: [],
  [IntegrationSetupStepType.STEP_TYPE_SFTP_CREDENTIALS_V2]: [],
  [IntegrationSetupStepType.STEP_TYPE_OAUTH]: ["customDomain", "linkChoiceStepOptionName"],
  [IntegrationSetupStepType.STEP_TYPE_OAUTH_CLIENT_CREDENTIALS]: [
    "oauthClientID",
    "oauthClientSecret",
  ],
  [IntegrationSetupStepType.STEP_TYPE_OVERRIDE_OAUTH_TOKEN_URL_AND_REFRESH_TOKEN]: [
    "oauthRefreshToken",
    "overrideOAuthTokenUrl",
  ],
  [IntegrationSetupStepType.STEP_TYPE_ADDITIONAL_AUTH]: ["additionalAuthFields"],
  [IntegrationSetupStepType.STEP_TYPE_TEXT_ONLY]: [],
  [IntegrationSetupStepType.STEP_TYPE_MFA_CODE]: ["mfaCode"],
  [IntegrationSetupStepType.STEP_TYPE_BASE_URL]: ["baseURL"],
  [IntegrationSetupStepType.STEP_TYPE_URL_PARSER]: [
    "baseURL",
    "linkChoiceStepOptionName",
    "endUserAccountType",
    "customDomain",
    "fullURL",
  ],
  [IntegrationSetupStepType.STEP_TYPE_ORG_CONSENT]: [],
  [IntegrationSetupStepType.STEP_TYPE_ORG_PURCHASE]: [],
  [NonStepPageType.SELECTIVE_SYNC_PAGE]: ["syncConfigurations"],
  [IntegrationSetupStepType.STEP_TYPE_CHOICE]: ["linkChoiceStepOptionName", "endUserAccountType"],
  [IntegrationSetupStepType.STEP_TYPE_TRELLO_OAUTH1]: ["customDomain"],
  [IntegrationSetupStepType.STEP_TYPE_WHITELIST_IP_ADDRESS]: [],
} as const;

type RawReadableFormDataPropertiesForStepType = typeof IterableReadableProperties;

export type ReadableFormDataPropertiesForStepType = {
  [Key in keyof RawReadableFormDataPropertiesForStepType]: RawReadableFormDataPropertiesForStepType[Key][number];
};

export type WritableFormDataPropertiesForStepType = ReadableFormDataPropertiesForStepType & {
  [IntegrationSetupStepType.STEP_TYPE_OAUTH]: never;
};

export type TypeForLinkingFlowFormDataProperty<T extends keyof LinkedAccountFormData> =
  LinkedAccountFormData[T];


export const createInitialFormData = (): RegisteredUserFormData => ({
  registered_user_secrets: undefined,
});


type Props = {
  linkingFlowPayload: IntegrationSetupPayload;
};

/**
 * Root component of a single IntegrationSetupStepPath.
 *
 * Manages state for the IntegrationSetupStepPath,
 * handles submitting, and passes appropriate props into a context that is used by each
 * individual IntegrationSetupStep.
 */
const ConnectorSetupPageRoot = ({ linkingFlowPayload }: Props) => {
  // destructure fields from linking flow payload, with some modifications and type assertions
  const connector = linkingFlowPayload.connector!;
  const authOption = linkingFlowPayload.auth_option;
  const registeredUser = linkingFlowPayload.registered_user!;
  const shouldBlockOauthOnCookies = linkingFlowPayload.should_block_oauth_on_cookies ?? true;

  const [registeredUserFormData, setRegisteredUserFormData] = useState<RegisteredUserFormData>(
    linkingFlowPayload.registered_user_form_data ?? createInitialFormData()
  );

  // hooks
  const {
    linkToken,
    apiBaseURL,
    onExit,
    getNextPageAndSetPage,
    navigateToPreviousEntryInHistory,
  } = useAgentHandlerLinkModalContext();

  // event handlers
  const getFormDataProperty = useCallback(
    (property: string) => registeredUserFormData["registered_user_secrets"]?.[property],
    [registeredUserFormData]
  );

  const setFormDataProperties = useCallback((property: string, value: string) => {
    setRegisteredUserFormData((prev) => ({
      ...prev,
      registered_user_secrets: {
        ...prev.registered_user_secrets,
        [property]: value,
      },
    }));
  }, [setRegisteredUserFormData]);

  // handles submitting the current step path
  const submitRegisteredUserFormData = () => {
    getNextPageAndSetPage({
      selected_auth_option: authOption,
      registered_user_form_data: registeredUserFormData,
    });
  };

  const navigateToPreviousStep = () => {
    navigateToPreviousEntryInHistory();
  };

  const navigateToNextStep = () => {
    submitRegisteredUserFormData();
  };


  return (
    <IntegrationSetupContext.Provider
      value={{
        connector,
        authOption,
        registeredUser,
        apiBaseURL,
        linkToken,
        onExit,
        getFormDataProperty,
        setFormDataProperties,
        navigateToPreviousStep,
        navigateToNextStep,
        submitRegisteredUserFormData,
        registeredUserSecrets: registeredUserFormData.registered_user_secrets,
        shouldBlockOauthOnCookies,
      }}
    >
      <ConnectorSetupStepSwitcher authOption={authOption} />
    </IntegrationSetupContext.Provider>
  );
};

export default ConnectorSetupPageRoot;
